import React, {
  createContext,
  FC,
  useMemo,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ProjectsContext } from 'src/app/providers/ProjectProvider';
import ProjectsService from 'src/shared/api/projects/ProjectsService';
import TableService from 'src/shared/api/table/TableService';
import TokenService from 'src/shared/api/token/TokenService';
import { TABLE_SOCKET_URL } from 'src/shared/config';
import { setError, setSuccess, setWarning, sleep } from 'src/shared/lib/utils';
import {
  initTableParts,
  rowsLimit,
  tableContextDefaultState,
} from 'src/shared/store/table/constants';
import { parseSearchInputToFilters, WsFilterCondition } from 'src/shared/store/table/searchFilters';
import { getProtectedBusinessRuleColumns } from 'src/shared/store/table/protectedColumns';
import {
  createEmptyRow,
  createEmptyRows,
  createRowsBatchData,
  loadTopScroll,
  parseRowsBatchDataRes,
  scrollTableToBottom,
} from 'src/shared/store/table/helpers';
import {
  ITableContext,
  ListItem,
  SaveRowsOptions,
  SortItem,
  TablePartsType,
} from 'src/shared/store/table/types';
import { UserContext } from 'src/shared/store/user';
import { canWriteData } from 'src/shared/store/user/permissions';
import { useUndoableState } from 'src/shared/hooks/useUndoableState';

export const TableContext = createContext<ITableContext>(tableContextDefaultState);

interface ITableProviderProps {
  children: ReactNode;
}

export const TableProvider: FC<ITableProviderProps> = ({ children }) => {
  const ref = useRef<TablePartsType>(initTableParts);
  const addRowsRef = useRef<number>(0);
  const socket = useRef<WebSocket | null>(null);

  const {
    tableData: _tableData,
    isLoading: _isLoading,
    isEdit: _is_edit,
  } = tableContextDefaultState;

  const { tableSettings, setSelectedForm, setTableSettings } = useContext(ProjectsContext);
  const { user } = useContext(UserContext);
  const canWriteTableData = canWriteData(user) && !tableSettings?.workflowReadOnly;
  const protectedRuleColumns = useMemo(
    () => getProtectedBusinessRuleColumns(tableSettings?.businessRules),
    [tableSettings?.businessRules],
  );

  const {
    value: tableData,
    setValue: setTableData,
    undo,
    canUndo,
  } = useUndoableState<ListItem[]>({
    initialValue: _tableData,
    maxHistory: 100000,
  });

  // Реф для актуального состояния tableData, чтобы onmessage всегда видел последнюю версию
  const tableDataRef = useRef<ListItem[]>(tableData);
  useEffect(() => {
    tableDataRef.current = tableData;
  }, [tableData]);

  const [initialData, setInitialData] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(_isLoading);
  const [sort, setSort] = useState<SortItem | null>();
  const [search, setSearch] = useState<string>('');
  const [tableFilters, setTableFilters] = useState<WsFilterCondition[]>([]);
  const [isEdit, setIsEdit] = useState<boolean>(_is_edit);
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const [workflowRefreshToken, setWorkflowRefreshToken] = useState(0);

  const bumpWorkflowRefresh = () => {
    setWorkflowRefreshToken(prev => prev + 1);
  };

  useEffect(() => {
    if (!canWriteTableData && isEdit) {
      setIsEdit(false);
    }
  }, [canWriteTableData, isEdit]);

  useEffect(() => {
    if (!tableSettings || !tableSettings.formStructure?.length) {
      return;
    }

    setTableData(_tableData);
    setInitialData('');
    setSort(null);
    setSearch('');
    setTableFilters([]);
    setIsEdit(false);
    setSelectedRowId(null);

    socket.current = new WebSocket(TABLE_SOCKET_URL);
    socket.current.onopen = () => getData({ page: 1 });

    socket.current.onmessage = async ({ data }) => {
      let tableInfo: any;
      try {
        tableInfo = JSON.parse(data);
      } catch {
        setIsLoading(false);
        setTableData([]);
        setInitialData('');
        ref.current = initTableParts;
        if (typeof data === 'string') {
          const message = data.replace(/^Error \d+:\s*/i, '').trim();
          setError(message || 'Ошибка загрузки данных таблицы');
        } else {
          setError('Ошибка загрузки данных таблицы');
        }
        return;
      }

      if (!data || tableInfo?.data === null) {
        if (ref.current.totalRows > 0) {
          setTableData([]);
          setInitialData('');
          return;
        }

        if (tableSettings) {
          setTableData([createEmptyRow(tableSettings)]);
        } else {
          setTableData([]);
        }
        setInitialData('');
        ref.current = initTableParts;
        return;
      }

      const { currentPage, totalPages, totalRows } = tableInfo;
      const props = { totalPages, totalRows };

      try {
        const rawData = tableInfo?.data || [];
        const list: ListItem[] = rawData.map((v: any) => ({
          ...v,
          actions: { isEdit: false, isRemoved: false },
        }));

        const loadedPages = ref.current.loadedPages;
        const currentTableData = tableDataRef.current; // Всегда актуальное состояние

        if (loadedPages[0] > currentPage) {
          ref.current = { loadedPages: [currentPage, ...loadedPages], ...props };
          setTableData([...list, ...currentTableData]);
          setInitialData(prev => {
            const prevData = prev ? JSON.parse(prev) : [];
            return JSON.stringify([...tableInfo.data, ...prevData]);
          });
          setIsLoading(false);

          await sleep(100);
          loadTopScroll();
          return;
        }

        if (loadedPages[loadedPages.length - 1] < currentPage) {
          const newRows = addRowsRef.current;
          ref.current = { loadedPages: [...loadedPages, currentPage], ...props };
          const extraRows = newRows && tableSettings ? createEmptyRows(newRows, tableSettings) : [];
          setTableData([...currentTableData, ...list, ...extraRows]);
          setInitialData(prev => {
            const prevData = prev ? JSON.parse(prev) : [];
            return JSON.stringify([...prevData, ...tableInfo.data]);
          });
          setIsLoading(false);

          if (newRows) {
            await sleep(100);
            scrollTableToBottom();
          }

          addRowsRef.current = 0;
          return;
        }

        // Перезаписываем данные
        ref.current = { loadedPages: [currentPage], ...props };
        setTableData(list);
        setInitialData(JSON.stringify(tableInfo.data));
        setIsLoading(false);
      } catch (e) {
        setError('Ошибка обработки данных');
        setIsLoading(false);
      }
    };
  }, [tableSettings, user]);

  useEffect(() => {
    if (!tableData.length) {
      setSelectedRowId(null);
      return;
    }

    setSelectedRowId(prev => {
      if (typeof prev === 'number' && tableData.some(row => row.id === prev)) {
        return prev;
      }
      const firstRowId = tableData.find(row => typeof row.id === 'number')?.id;
      return typeof firstRowId === 'number' ? firstRowId : null;
    });
  }, [tableData]);

  const resetTable = () => {
    setSelectedForm && setSelectedForm(null);
    setTableSettings && setTableSettings(null);
    setTableData([]);
    setInitialData('');
    setSearch('');
    setTableFilters([]);
    setSelectedRowId(null);
    ref.current = initTableParts;
  };

  const getData = ({
    page,
    newSort,
    newSearch,
    newFilters,
  }: {
    page?: number;
    newSort?: SortItem;
    newSearch?: string;
    newFilters?: WsFilterCondition[];
  }) => {
    if (!tableSettings) return;
    const access = TokenService.getAccessToken();
    const ws = socket.current;
    if (!access || !ws || ws.readyState !== WebSocket.OPEN) {
      return;
    }

    const currentSort = newSort || sort;
    const currentSearch = typeof newSearch === 'string' ? newSearch : search;
    const currentFilters = Array.isArray(newFilters) ? newFilters : tableFilters;
    const { search: plainSearch, filters: parsedSearchFilters } =
      parseSearchInputToFilters(currentSearch);
    const filters = [...currentFilters, ...parsedSearchFilters];

    const params = new URLSearchParams();
    params.set('page', String(page || 1));
    params.set('limit', String(rowsLimit));
    params.set('tableID', String(tableSettings.id));
    params.set('tokenString', access);
    params.set('search', plainSearch);

    if (currentSort) {
      params.set('sortField', currentSort.name.toString());
      params.set('sortDirection', currentSort.sort);
    }

    if (filters.length) {
      params.set('filters', JSON.stringify(filters));
    }

    ws.send(params.toString());
  };

  const getBottomContent = () => {
    const { totalPages, loadedPages } = ref.current;
    const lastPage = loadedPages[loadedPages.length - 1];

    if (lastPage < totalPages) {
      getData({ page: lastPage + 1 });
    }
  };

  const getTopContent = () => {
    const { loadedPages } = ref.current;
    const firstPage = loadedPages[0];

    if (firstPage !== 1) {
      getData({ page: firstPage - 1 });
    }
  };

  const refreshTable = () => {
    if (!tableSettings) return;
    setIsLoading(true);
    ref.current = initTableParts;
    getData({ page: 1 });
  };

  const resetTableData = () => {
    if (!initialData) {
      if (tableSettings) {
        setTableData([createEmptyRow(tableSettings)]);
      } else {
        setTableData([]);
      }
      return;
    }

    const list: ListItem[] = JSON.parse(initialData).map((v: any) => ({
      ...v,
      actions: { isEdit: false, isRemoved: false },
    }));
    setTableData(list);
  };

  const addEmptyRows = (count: number) => {
    if (!canWriteTableData) return;
    if (!tableSettings) return;
    const { loadedPages, totalPages } = ref.current;
    const currentTableData = tableDataRef.current;

    if (loadedPages.includes(totalPages) || !totalPages) {
      const newRows = createEmptyRows(count, tableSettings);
      setTableData([...currentTableData, ...newRows]);
      scrollTableToBottom();
      return;
    }

    const page = totalPages;
    setIsLoading(true);
    addRowsRef.current = count;
    getData({ page: page - 1 });
    ref.current = initTableParts;
    getData({ page });
  };

  const addEmptyTopRows = () => {
    if (!canWriteTableData) return;
    if (!tableSettings) return;
    const currentTableData = tableDataRef.current;
    const newRow = createEmptyRow(tableSettings);
    setTableData([newRow, ...currentTableData]);
  };

  const handleSetSort = (newSort: SortItem) => {
    setSort(newSort);
    getData({ page: 1, newSort });
  };

  const handleSetSearch = (newSearch: string) => {
    setSearch(newSearch);
    getData({ page: 1, newSearch });
  };

  const handleSetTableFilters = (newFilters: WsFilterCondition[]) => {
    setTableFilters(newFilters);
    getData({ page: 1, newFilters });
  };

  const saveRows = async (options?: SaveRowsOptions) => {
    if (!canWriteTableData) {
      setError('Форма доступна только для чтения в текущем статусе бизнес-процесса');
      return;
    }
    if (!tableSettings?.id) {
      return;
    }

    setIsLoading(true);
    const currentTableData = tableDataRef.current;

    const clearlyData = currentTableData.map(el => {
      const temp = { ...el };
      delete temp['actions'];
      delete temp['isDateOpen'];
      delete temp['isOpen'];
      return temp;
    });

    if (JSON.stringify(clearlyData) === initialData) {
      setIsLoading(false);
      return;
    }

    const params = createRowsBatchData({
      initialData,
      clearlyData,
      readOnlyColumns: protectedRuleColumns,
    });

    try {
      const { data } = await TableService.batchRows({ data: params, table_id: tableSettings.id });
      parseRowsBatchDataRes({ data, setTableData, setInitialData, initialData });
      bumpWorkflowRefresh();

      if (tableSettings.workflowSettings?.enabled && !tableSettings.isDirectory) {
        try {
          const { data: workflowData } = await ProjectsService.getFormWorkflow(tableSettings.id);
          setTableSettings({
            ...tableSettings,
            workflowSettings: workflowData.workflowSettings || tableSettings.workflowSettings,
            workflowCurrentStatus: workflowData.currentStatus,
            workflowReadOnly: workflowData.readOnly,
          });
        } catch (workflowError: any) {
          if (workflowError?.response?.status === 403) {
            setError(
              workflowError?.response?.data?.error || 'Доступ к форме закрыт новым статусом',
            );
          }
          console.error('Failed to refresh form workflow after save', workflowError);
        }
      }

      if (!options?.includeDependentRules) {
        return;
      }

      const successfulCreateRows = data.createResults
        .filter(result => result.success)
        .map(result => Number(result?.data?.id))
        .filter(id => Number.isFinite(id) && id > 0)
        .map(id => Math.floor(id));
      const successfulUpdateRows = data.updateResults
        .filter(result => result.success)
        .map(result => Number(result?.data?.id))
        .filter(id => Number.isFinite(id) && id > 0)
        .map(id => Math.floor(id));
      const changedRowIDs = Array.from(new Set([...successfulCreateRows, ...successfulUpdateRows]));

      if (!changedRowIDs.length) {
        return;
      }

      const { data: recalculationResult } = await TableService.recalculateTableRules(
        tableSettings.id,
        {
          includeDependent: true,
          rowIds: changedRowIDs,
        },
      );

      const processedTables = recalculationResult?.processedTables || 0;
      const processedRows = recalculationResult?.processedRows || 0;
      const failedRows = recalculationResult?.failedRows || 0;

      setSuccess(
        `Бизнес-правила пересчитаны: таблиц=${processedTables}, строк=${processedRows}, ошибок=${failedRows}`,
      );

      if (recalculationResult?.warnings?.length) {
        const uniqueWarnings = Array.from(new Set(recalculationResult.warnings.filter(Boolean)));
        const preview = uniqueWarnings.slice(0, 3).join('; ');
        const extraCount = uniqueWarnings.length - Math.min(uniqueWarnings.length, 3);
        const suffix = extraCount > 0 ? ` (+${extraCount})` : '';
        setWarning(`Предупреждения перерасчёта правил: ${preview}${suffix}`);
      }
    } catch (e) {
      setError('Не удалось сохранить данные таблицы');
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <TableContext.Provider
      value={{
        isLoading,
        tableData,
        selectedRowId,
        setSelectedRowId,
        workflowRefreshToken,
        bumpWorkflowRefresh,
        isEdit,
        setIsEdit,
        setIsLoading,
        setTableData,
        resetTableData,
        addEmptyRows,
        addEmptyTopRows,
        saveRows,
        setSort: handleSetSort,
        setSearch: handleSetSearch,
        tableFilters,
        setTableFilters: handleSetTableFilters,
        getBottomContent,
        getTopContent,
        refreshTable,
        resetTable,
        totalRows: ref.current.totalRows || 0,
        undo,
        canUndo,
      }}
    >
      {children}
    </TableContext.Provider>
  );
};
